 import { useLocation, Link } from "react-router-dom";
 import { useEffect } from "react";
 import { motion } from "framer-motion";
 import { Home, Search, Phone, ArrowRight } from "lucide-react";
 import { Header } from "@/components/layout/Header";
 import { Footer } from "@/components/layout/Footer";
 import { Button } from "@/components/ui/button";
 import { usePageMeta } from "@/hooks/use-page-meta";
 
 const popularLinks = [
   { label: "Get a Quote", href: "/quote", description: "Request pricing for your project" },
   { label: "Products", href: "/products", description: "Browse our racking systems" },
   { label: "Services", href: "/services", description: "New, used, installation & more" },
   { label: "Locations", href: "/locations", description: "Find your nearest hub" },
 ];
 
 const NotFound = () => {
   const location = useLocation();
   usePageMeta({
     title: "Page Not Found",
     description: "The page you're looking for doesn't exist. Browse our pallet racking products, services, or request a free quote.",
   });
 
   return (
     <div className="min-h-screen flex flex-col">
       <Header />
       <main className="flex-1 bg-background">
         <div className="container py-16 md:py-24">
           <motion.div
             initial={{ opacity: 0, y: 20 }}
             animate={{ opacity: 1, y: 0 }}
             className="max-w-2xl mx-auto text-center"
           >
             {/* 404 Icon */}
             <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-muted mb-8">
               <Search className="h-12 w-12 text-muted-foreground" />
             </div>
 
             <h1 className="text-5xl md:text-6xl font-bold text-foreground mb-4">404</h1>
             <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-4">
               Page Not Found
             </h2>
             <p className="text-lg text-muted-foreground mb-8">
               Sorry, we couldn't find the page you're looking for. The page may have been moved or no longer exists.
             </p>
 
             {/* Primary CTA */}
             <div className="flex flex-wrap justify-center gap-4 mb-12">
               <Button variant="accent" size="lg" asChild>
                 <Link to="/">
                   <Home className="h-4 w-4 mr-2" />
                   Go to Homepage
                 </Link>
               </Button>
               <Button variant="outline" size="lg" asChild>
                 <a href="tel:1-888-315-7225">
                   <Phone className="h-4 w-4 mr-2" />
                   Call Us
                 </a>
               </Button>
             </div>
 
             {/* Popular Links */}
             <div className="bg-muted rounded-xl p-6">
               <h3 className="font-semibold text-foreground mb-4">Popular Pages</h3>
               <div className="grid sm:grid-cols-2 gap-3">
                 {popularLinks.map((link) => (
                   <Link
                     key={link.href}
                     to={link.href}
                     className="flex items-center justify-between p-4 bg-card rounded-lg border border-border hover:border-accent/50 hover:shadow-card transition-all group"
                   >
                     <div className="text-left">
                       <div className="font-medium text-foreground group-hover:text-accent transition-colors">
                         {link.label}
                       </div>
                       <div className="text-sm text-muted-foreground">
                         {link.description}
                       </div>
                     </div>
                     <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-accent group-hover:translate-x-1 transition-all" />
                   </Link>
                 ))}
               </div>
             </div>
           </motion.div>
         </div>
       </main>
       <Footer />
     </div>
   );
 };
 
 export default NotFound;
